/**
 * PURPOSE: Pestaña "Cómputo" del evento (FTR-0066): cargar el puesto final y la participación de cada institución por
 *   disciplina y ver el ranking general del Cómputo General.
 * RESPONSIBILITIES:
 *   - Pedir el tablero del evento (disciplinas + ranking) y mostrar sus estados: cargando, error con reintentar,
 *     503 SCHEMA_NOT_READY (falta el SQL) y 409 COMPUTO_DISABLED (flag apagado).
 *   - Al elegir una disciplina, pedirla y mantener su borrador; avisar (confirmación) si se cambia de disciplina con
 *     cambios sin guardar; "Descartar cambios".
 *   - Guardar la disciplina con TODAS sus instituciones inscritas (reemplazo completo), mostrar "Guardado" / "Sin
 *     cambios" o el mensaje del backend, y volver a pedir el tablero para refrescar el ranking.
 *   - Enlace "Ver en Hayllis" a la página pública del cómputo del evento.
 *   La visibilidad de la pestaña (solo rol 1 y flag encendido) la decide ListCategoriasSection; el backend vuelve a
 *   exigir rol 1, acceso al evento y flag en cada petición.
 * COLLABORATORS: ListCategoriasSection.jsx, services/eventComputoService.js, ComputoDisciplineList.jsx,
 *   ComputoDisciplineEditor.jsx, ComputoRankingPreview.jsx, computoGeneral.utils.js.
 * ROUTE: winscore/src/area/ComputoGeneralArea/ComputoGeneralArea.jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FaCalculator, FaExternalLinkAlt, FaSyncAlt } from "react-icons/fa";

import {
  getEventComputoBoard,
  getEventComputoDiscipline,
  saveEventComputoDiscipline,
} from "../../services/eventComputoService.js";
import ComputoDisciplineEditor from "./ComputoDisciplineEditor.jsx";
import ComputoDisciplineList from "./ComputoDisciplineList.jsx";
import ComputoRankingPreview from "./ComputoRankingPreview.jsx";
import {
  buildDraft,
  buildEditorRows,
  buildHayllisComputoUrl,
  buildSaveEntries,
  readComputoError,
  summarizeEditorRows,
} from "./computoGeneral.utils.js";

const BOARD_ERROR_TITLES = {
  SCHEMA_NOT_READY: "Falta aplicar la actualización de base de datos",
  COMPUTO_DISABLED: "El Cómputo General no está habilitado para este evento",
};

const BOARD_ERROR_HINTS = {
  SCHEMA_NOT_READY: "Cuando se aplique el SQL del Cómputo General, vuelve a intentarlo.",
  COMPUTO_DISABLED: "Actívalo en Ajustes → «Habilitar Cómputo General» y guarda la configuración.",
};

const BoardMessage = ({ error, onRetry }) => {
  const code = error?.code;
  return (
    <div role="alert" className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
      <h3 className="text-lg font-black text-amber-900">{BOARD_ERROR_TITLES[code] || "No se pudo cargar el Cómputo General"}</h3>
      <p className="mt-2 text-sm font-semibold text-amber-800">{error?.message}</p>
      {BOARD_ERROR_HINTS[code] ? <p className="mt-1 text-sm text-amber-800">{BOARD_ERROR_HINTS[code]}</p> : null}
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-amber-700"
      >
        <FaSyncAlt />
        Reintentar
      </button>
    </div>
  );
};

const ComputoGeneralArea = () => {
  const { idevent } = useParams();
  const [board, setBoard] = useState({ status: "loading", data: null, error: null });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [discipline, setDiscipline] = useState({ status: "idle", data: null, error: null });
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const disciplineRequest = useRef(0);

  const loadBoard = useCallback(
    async ({ silent = false } = {}) => {
      if (!idevent) return;
      if (silent) setRefreshing(true);
      else setBoard((current) => ({ ...current, status: "loading", error: null }));
      try {
        const data = await getEventComputoBoard({ idevent });
        setBoard({ status: "ready", data, error: null });
        setRefreshError(null);
      } catch (error) {
        const info = readComputoError(error, "No se pudo cargar el Cómputo General.");
        if (silent) setRefreshError(info.message);
        else setBoard({ status: "error", data: null, error: info });
      } finally {
        if (silent) setRefreshing(false);
      }
    },
    [idevent]
  );

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const loadDiscipline = useCallback(
    async (idsport) => {
      const requestId = disciplineRequest.current + 1;
      disciplineRequest.current = requestId;
      setDiscipline({ status: "loading", data: null, error: null });
      setFeedback(null);
      try {
        const data = await getEventComputoDiscipline({ idevent, idsport });
        if (requestId !== disciplineRequest.current) return;
        setDiscipline({ status: "ready", data, error: null });
        setDraft(buildDraft(data?.institutions));
      } catch (error) {
        if (requestId !== disciplineRequest.current) return;
        setDiscipline({ status: "error", data: null, error: readComputoError(error, "No se pudo cargar la disciplina.") });
      }
    },
    [idevent]
  );

  const rows = useMemo(
    () =>
      discipline.status === "ready"
        ? buildEditorRows({ institutions: discipline.data?.institutions || [], draft, scoring: discipline.data?.scoring })
        : [],
    [discipline, draft]
  );
  const summary = useMemo(() => summarizeEditorRows(rows), [rows]);
  const dirty = summary.changed > 0;

  const handleSelect = (item) => {
    if (saving || String(item.idsport) === String(selected?.idsport)) return;
    if (
      dirty &&
      !window.confirm(
        `Hay cambios sin guardar en ${discipline.data?.discipline?.label || selected?.label}. ¿Descartarlos y abrir ${item.label}?`
      )
    ) {
      return;
    }
    setSelected(item);
    loadDiscipline(item.idsport);
  };

  const updateDraftRow = (institutionId, patch) => {
    setDraft((current) => {
      const key = String(institutionId);
      return { ...current, [key]: { ...(current[key] || { place: "", participation: true }), ...patch } };
    });
    setFeedback(null);
  };

  const handleDiscard = () => {
    setDraft(buildDraft(discipline.data?.institutions));
    setFeedback(null);
  };

  const handleSave = async () => {
    if (!selected || discipline.status !== "ready" || summary.invalid > 0 || !dirty) return;
    setSaving(true);
    setFeedback(null);
    try {
      const result = await saveEventComputoDiscipline({ idevent, idsport: selected.idsport, entries: buildSaveEntries(rows) });
      const { changed, ...data } = result || {};
      if (Array.isArray(data.institutions)) {
        setDiscipline({ status: "ready", data, error: null });
        setDraft(buildDraft(data.institutions));
      } else {
        loadDiscipline(selected.idsport);
      }
      setFeedback(changed === false ? { type: "info", message: "Sin cambios" } : { type: "success", message: "Guardado" });
      loadBoard({ silent: true });
    } catch (error) {
      const info = readComputoError(error, "No se pudo guardar la disciplina.");
      const institution = info.details?.idinstitution
        ? (discipline.data?.institutions || []).find((item) => Number(item.id) === Number(info.details.idinstitution))
        : null;
      const suffix = institution ? ` (${institution.abbreviation || institution.name})` : "";
      setFeedback({ type: "error", message: `${info.message}${suffix}` });
    } finally {
      setSaving(false);
    }
  };

  if (board.status === "loading" && !board.data) {
    return (
      <div role="status" className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-6 text-center text-sm font-bold text-blue-700">
        Cargando Cómputo General...
      </div>
    );
  }

  if (board.status === "error") {
    return <BoardMessage error={board.error} onRetry={() => loadBoard()} />;
  }

  const boardData = board.data || {};
  const disciplines = Array.isArray(boardData.disciplines) ? boardData.disciplines : [];

  return (
    <section className="grid gap-5">
      <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white">
              <FaCalculator />
            </span>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Cómputo General</h2>
              <p className="text-sm text-gray-500">
                Carga el puesto final y la participación de cada institución por disciplina. Hayllis 2.0 suma los puntos en el
                ranking general del evento.
              </p>
            </div>
          </div>
          <a
            href={buildHayllisComputoUrl(idevent)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-bold text-blue-700 transition hover:bg-blue-100"
          >
            <FaExternalLinkAlt />
            Ver en Hayllis
          </a>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
        <ComputoDisciplineList
          disciplines={disciplines}
          selectedSportId={selected?.idsport}
          onSelect={handleSelect}
          disabled={saving}
        />

        <div className="min-w-0">
          {!selected ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-semibold text-slate-500">
              Elige una disciplina para cargar sus puestos y la participación de sus instituciones.
            </div>
          ) : null}

          {selected && discipline.status === "loading" ? (
            <div role="status" className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-6 text-center text-sm font-bold text-blue-700">
              Cargando {selected.label}...
            </div>
          ) : null}

          {selected && discipline.status === "error" ? (
            <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
              <p className="text-sm font-bold text-red-700">{discipline.error?.message}</p>
              <button
                type="button"
                onClick={() => loadDiscipline(selected.idsport)}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-red-700"
              >
                <FaSyncAlt />
                Reintentar
              </button>
            </div>
          ) : null}

          {selected && discipline.status === "ready" ? (
            <ComputoDisciplineEditor
              data={discipline.data}
              rows={rows}
              summary={summary}
              dirty={dirty}
              saving={saving}
              feedback={feedback}
              onPlaceChange={(id, place) => updateDraftRow(id, { place })}
              onParticipationChange={(id, participation) => updateDraftRow(id, { participation })}
              onSave={handleSave}
              onDiscard={handleDiscard}
            />
          ) : null}
        </div>
      </div>

      {refreshError ? (
        <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-bold text-amber-800">
          No se pudo actualizar el ranking: {refreshError}
        </p>
      ) : null}

      <ComputoRankingPreview
        ranking={Array.isArray(boardData.ranking) ? boardData.ranking : []}
        lastUpdate={boardData.lastUpdate}
        refreshing={refreshing}
        onRefresh={() => loadBoard({ silent: true })}
      />
    </section>
  );
};

export default ComputoGeneralArea;
